/**
 * Prose dates — the ONE shared home for day/date language (lifted from
 * cmHome.ts so every surface renders dates in words, never a raw ISO
 * timestamp on screen). cmHome re-exports these for its existing importers.
 *
 * Two registers:
 *   - proseDay / yourNotePhrase — RELATIVE recency ("today", "yesterday", a
 *     weekday, then "June 30"). For recency signals (a note age, last touched).
 *   - proseDate — ABSOLUTE with year ("August 27, 2026"). For record facts
 *     (med started, appointment date, coverage verified) — historical OR
 *     future, where a relative phrase would mislead.
 */

export const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function parseIsoDate(iso: string): Date {
  // Local-midnight parse (never UTC): '2026-07-06' → local July 6.
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/**
 * A UTC ISO TIMESTAMP's LOCAL calendar day ('YYYY-MM-DD'). Never slice a
 * timestamp for its date: an event at 9pm Eastern on July 5 is July 6 in
 * UTC, and a raw slice misdates it. Timestamps parse to a local Date
 * here; date-only strings keep using parseIsoDate (local-midnight).
 */
export function localDayOf(timestamp: string): string {
  const d = new Date(timestamp);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Whole days between two ISO dates (today - then). */
export function daysBetween(thenIso: string, todayIso: string): number {
  const ms = parseIsoDate(todayIso).getTime() - parseIsoDate(thenIso).getTime();
  return Math.round(ms / 86_400_000);
}

/** 'today' | 'yesterday' | a weekday name (within the past week) | 'June 30'. */
export function proseDay(dateIso: string, todayIso: string): string {
  const diff = daysBetween(dateIso, todayIso);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  const d = parseIsoDate(dateIso);
  if (diff < 7) return WEEKDAYS[d.getDay()] ?? 'earlier';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * The forward mirror of proseDay: 'today' | a weekday name (within the
 * coming week) | 'September 22' (no year — for anything further out).
 * proseDay is past-only (a future date there always reads as "today",
 * the wrong side of zero); this is for a session that HASN'T happened
 * yet (the caseload row's nearest-upcoming-session line) — near dates
 * read as a plain weekday, and a biweekly client's off-week gap reads
 * as a real date instead of looking exactly like every other week.
 */
export function proseUpcomingDay(dateIso: string, todayIso: string): string {
  const diff = daysBetween(todayIso, dateIso); // dateIso - todayIso
  if (diff <= 0) return 'today';
  const d = parseIsoDate(dateIso);
  if (diff < 7) return WEEKDAYS[d.getDay()] ?? 'soon';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "your Tuesday note" / "your note today" / "your note from June 30". */
export function yourNotePhrase(dateIso: string, todayIso: string): string {
  const diff = daysBetween(dateIso, todayIso);
  if (diff <= 0) return 'your note today';
  if (diff === 1) return 'your note yesterday';
  if (diff < 7) {
    const d = parseIsoDate(dateIso);
    return `your ${WEEKDAYS[d.getDay()] ?? 'earlier'} note`;
  }
  return `your note from ${proseDay(dateIso, todayIso)}`;
}

/**
 * Absolute prose date: 'August 27, 2026'. For record facts (a med start, an
 * appointment, a verification) — historical OR future, where a relative
 * phrase would mislead — never a bare ISO string on screen. Accepts a
 * date-only string or an ISO timestamp (its leading date is used).
 */
export function proseDate(dateIso: string): string {
  const d = parseIsoDate(dateIso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** A day still to come with its weekday: 'Fri 20 Nov' (LP-41, the
 *  Medications card's Starts and Stops; the time card's day form). */
export function weekdayDayMonth(dateIso: string): string {
  const d = parseIsoDate(dateIso);
  return `${WEEKDAYS[d.getDay()]!.slice(0, 3)} ${d.getDate()} ${MONTHS[d.getMonth()]!.slice(0, 3)}`;
}

/** A line's date in a Pre-billing table: 'Oct 3'. The heading above the
 *  table names the year; `withYear` adds it ('Oct 3, 2026') where the
 *  dates shown cross a year. The client's Pre-billing tab and the
 *  all-client page both use it. */
export function rowDate(dateIso: string, withYear = false): string {
  const d = parseIsoDate(dateIso);
  const day = `${MONTHS[d.getMonth()]!.slice(0, 3)} ${d.getDate()}`;
  return withYear ? `${day}, ${d.getFullYear()}` : day;
}

/** A wall-clock 'HH:MM' (or 'HH:MM:SS') as it reads: '14:30' is '2:30 PM',
 *  '00:05' is '12:05 AM'. Anything else passes through as given. */
export function fmtTime(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * Provenance register (founder polish 2026-07-06, extending the Overview
 * feed's treatment record-wide): relative ONLY for the freshest two days —
 * 'today' / 'yesterday' — and the ABSOLUTE prose date (with year) for
 * anything older. Weekday phrasing stays proseDay's recency register; a
 * provenance line never says "Tuesday" (a year-old Tuesday would mislead).
 * `todayIso` is INJECTED by the page (tests pass a fixed day; see
 * src/lib/today.ts) — when absent the date renders absolute, so no
 * component ever reaches for Date.now().
 */
export function proseDayOrDate(dateIso: string, todayIso?: string): string {
  if (todayIso !== undefined) {
    const diff = daysBetween(dateIso, todayIso);
    if (diff <= 0) return 'today';
    if (diff === 1) return 'yesterday';
  }
  return proseDate(dateIso);
}

/**
 * Sentence-composable form of proseDayOrDate: 'today' | 'yesterday' |
 * 'on July 5, 2026' — callers write "edited {when}" / "from your note
 * {when}" and the preposition rides only with the absolute arm.
 */
export function proseWhen(dateIso: string, todayIso?: string): string {
  const s = proseDayOrDate(dateIso, todayIso);
  return s === 'today' || s === 'yesterday' ? s : `on ${s}`;
}
